import { useEffect, useState } from "react"
import { Head, Link } from "@inertiajs/react"
import { ArrowRight, Bot, User } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { ChannelIcon } from "@/lib/channel-icons"
import { channelColor } from "@/lib/channel-colors"
import { cn } from "@/lib/utils"

type TodoRef = {
  id: number
  kind: string
  created_at: string
}

type Piece = {
  id: number
  name: string
  channel: string
  channel_name: string | null
  channel_type: string | null
  channel_color: string | null
  todos: TodoRef[]
}

const TODO_VERBS: Record<string, string> = {
  schedule_piece:  "Schedule the piece",
  decide_failed:        "Decide what to do",
  decide_bump_exceeded: "Decide — bump exceeded",
}

// Workflow order for pills on a single piece.
const TODO_ORDER: Record<string, number> = {
  schedule_piece: 1,
  decide_failed:        1,
  decide_bump_exceeded: 1,
}

function sortTodos<T extends { kind: string }>(todos: T[]): T[] {
  return [...todos].sort(
    (a, b) => (TODO_ORDER[a.kind] ?? 99) - (TODO_ORDER[b.kind] ?? 99),
  )
}

function countTodos(pieces: Piece[]): number {
  return pieces.reduce((n, p) => n + p.todos.length, 0)
}

export default function Todos({
  for_you,
  for_agent,
  initial_tab,
}: {
  for_you: Piece[]
  for_agent: Piece[]
  initial_tab: "user" | "agent"
}) {
  const [tab, setTab] = useState<"user" | "agent">(initial_tab)
  useEffect(() => {
    setTab(initial_tab)
  }, [initial_tab])

  const userCount  = countTodos(for_you)
  const agentCount = countTodos(for_agent)
  const active     = tab === "user" ? for_you : for_agent
  const tone       = tab === "user" ? "accent" : "signal"
  const emptyText  = tab === "user" ? "Nothing waiting on you." : "Nothing waiting on the agent."

  return (
    <>
      <Head title="Todos">
        <meta
          name="description"
          content="The two-tab inbox of pieces waiting on you or on the agent."
        />
        <meta property="og:title" content="Todos" />
        <meta
          property="og:description"
          content="The two-tab inbox of pieces waiting on you or on the agent."
        />
      </Head>
      <AppShell>
        <div className="border-b border-hairline pb-6">
          <h1>Todos</h1>
          <p className="mt-1">
            {userCount + agentCount} open todos across every piece.
          </p>
        </div>

        <nav className="mt-6 flex gap-6 border-b border-hairline text-sm">
          <TabButton
            active={tab === "user"}
            onClick={() => setTab("user")}
            icon={<User className="h-4 w-4" />}
            label="For you"
            count={userCount}
          />
          <TabButton
            active={tab === "agent"}
            onClick={() => setTab("agent")}
            icon={<Bot className="h-4 w-4" />}
            label="For the agent"
            count={agentCount}
            tone="signal"
          />
        </nav>

        <PieceList pieces={active} tone={tone} emptyText={emptyText} />
      </AppShell>
    </>
  )
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  count,
  tone = "accent",
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  count: number
  tone?: "accent" | "signal"
}) {
  const activeBorder = tone === "signal" ? "border-signal" : "border-accent"
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mb-px inline-flex cursor-pointer items-center gap-2 border-b-2 px-1 pb-3 transition-colors",
        active
          ? cn(activeBorder, "font-medium text-ink-display")
          : "border-transparent text-ink-muted hover:text-ink-display",
      )}
    >
      {icon}
      <span>{label}</span>
      <Badge tone={active ? tone : "muted"}>{count}</Badge>
    </button>
  )
}

function PieceList({
  pieces,
  tone,
  emptyText,
}: {
  pieces: Piece[]
  tone: "accent" | "signal"
  emptyText: string
}) {
  if (pieces.length === 0) {
    return <p className="mt-8 text-sm text-ink-muted">{emptyText}</p>
  }

  return (
    <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
      {pieces.map((p) => (
        <li key={p.id}>
          <Link
            href={`/pieces/${p.id}`}
            className="flex items-start gap-3 px-4 py-3 no-underline hover:bg-surface"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm">
                {(() => {
                  const cc = channelColor(p.channel_color)
                  return (
                    <Badge
                      tone="neutral"
                      className={cn("inline-flex items-center gap-1 border", cc.cardBg, cc.cardBorder, cc.text)}
                    >
                      <ChannelIcon channelType={p.channel_type} className="h-3 w-3" />
                      {p.channel_name ?? p.channel}
                    </Badge>
                  )
                })()}
                <span className="truncate font-medium text-ink-display">
                  {p.name}
                </span>
              </div>
              <PieceTodoRow todos={p.todos} tone={tone} />
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function PieceTodoRow({
  todos,
  tone,
}: {
  todos: TodoRef[]
  tone: "accent" | "signal"
}) {
  const sorted  = sortTodos(todos)
  const numbered = sorted.length > 1

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {sorted.map((t, idx) => (
        <span key={t.id} className="flex items-center gap-1.5">
          <TodoPill
            kind={t.kind}
            tone={tone}
            sequence={numbered ? idx + 1 : null}
          />
          {numbered && idx < sorted.length - 1 && (
            <ArrowRight className="h-3 w-3 text-ink-muted" />
          )}
        </span>
      ))}
    </div>
  )
}

function TodoPill({
  kind,
  tone,
  sequence,
}: {
  kind: string
  tone: "accent" | "signal"
  sequence?: number | null
}) {
  const border = tone === "signal" ? "border-signal bg-signal/10" : "border-accent bg-accent/10"
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-ink-display",
        border,
      )}
    >
      {sequence != null && (
        <span className="font-mono text-[10px] text-ink-muted">{sequence}</span>
      )}
      <span>{TODO_VERBS[kind] ?? kind}</span>
    </span>
  )
}
